import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI, getBackendUrl } from '../api';
import '../App.css';

function ClientDetail({ onLogout }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [session, setSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadClientDetail();
  }, [clientId]);

  const loadClientDetail = async () => {
    try {
      const response = await adminAPI.getClient(clientId);
      setClient(response.data.client);
      setSession(response.data.session);
      setDocuments(response.data.documents);
      setAuditLogs(response.data.audit_logs);
      setError('');
    } catch (err) {
      setError('Failed to load client details');
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyID = async (verified) => {
    try {
      await adminAPI.verifyID(clientId, verified);
      setSuccessMessage(`ID ${verified ? 'verified' : 'unverified'} successfully`);
      loadClientDetail();
    } catch (err) {
      setError('Failed to update ID verification status');
    }
  };

  const handleMarkComplete = async () => {
    if (!window.confirm('Mark this client as 100% complete? This locks the record so it stays in your dashboard permanently.')) {
      return;
    }
    try {
      await adminAPI.markComplete(clientId);
      setSuccessMessage('Client marked as complete (100%). Record is now locked from auto-expiry.');
      loadClientDetail();
    } catch (err) {
      setError('Failed to mark client as complete');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading client details...</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="loading-screen">
        <p>Client not found</p>
        <button className="btn btn-primary" onClick={() => navigate('/admin/dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-gray-50)' }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'var(--primary-navy)',
        color: 'var(--text-white)',
        padding: '1.5rem 2rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/logo.webp" alt="ST Books" style={{ width: '48px', height: '48px', borderRadius: '50%', marginRight: '1rem' }} />
            <button 
              onClick={() => navigate('/admin/dashboard')} 
              className="btn btn-outline"
              data-testid="back-button"
              style={{ borderColor: 'white', color: 'white', marginRight: '1rem' }}
            >
              ← Back
            </button>
            <h1 style={{ color: 'var(--text-white)', fontSize: '1.5rem', display: 'inline' }}>
              {client.business_name || 'Client Details'}
            </h1>
          </div>
          <button onClick={onLogout} className="btn btn-outline" style={{ borderColor: 'white', color: 'white' }}>
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: '1400px', margin: '2rem auto', padding: '0 2rem' }}>
        <button className="btn btn-primary" data-testid="client-agreement-history-button" style={{marginBottom:'1.5rem'}} onClick={() => navigate(`/admin/agreements/${clientId}`)}>Agreements & rate changes</button>
        {successMessage && (
          <div className="alert alert-success" data-testid="success-message">
            {successMessage}
            <button 
              onClick={() => setSuccessMessage('')}
              style={{ float: 'right', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {/* Business Details */}
          <div className="card">
            <div className="card-header">
              <h3>Business Information</h3>
            </div>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <strong style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Business Name</strong>
                <p style={{ margin: '0.25rem 0 0 0' }}>{client.business_name}</p>
              </div>
              <div>
                <strong style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>ABN</strong>
                <p style={{ margin: '0.25rem 0 0 0' }}>{client.abn}</p>
              </div>
              <div>
                <strong style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Contact Name</strong>
                <p style={{ margin: '0.25rem 0 0 0' }}>{client.contacts?.name || 'N/A'}</p>
              </div>
              <div>
                <strong style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Email</strong>
                <p style={{ margin: '0.25rem 0 0 0' }}>{client.contacts?.email || 'N/A'}</p>
              </div>
              <div>
                <strong style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Phone</strong>
                <p style={{ margin: '0.25rem 0 0 0' }}>{client.contacts?.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className="card">
            <div className="card-header">
              <h3>Verification</h3>
            </div>
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>ID Verification Status</strong>
                <p style={{ margin: '0.5rem 0' }}>
                  {client.id_verified ? (
                    <span className="badge badge-success">✓ Verified</span>
                  ) : (
                    <span className="badge badge-warning">Pending Verification</span>
                  )}
                </p>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Onboarding Progress</strong>
                <p style={{ margin: '0.5rem 0' }} data-testid="progress-display">
                  {(() => {
                    // Determine effective progress: prefer session data; fall back to client-level flags
                    const effectiveProgress = session?.progress
                      ?? (client.manually_completed || client.id_verified ? 100 : (session ? 0 : 95));
                    const effectiveStatus = session?.status
                      || (client.manually_completed || client.id_verified ? 'completed' : (session ? 'unknown' : 'signed'));
                    return (
                      <span className={`badge ${effectiveProgress >= 100 ? 'badge-success' : 'badge-warning'}`}>
                        {effectiveProgress}% — {effectiveStatus}
                      </span>
                    );
                  })()}
                  {!session && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      (session archived)
                    </span>
                  )}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {!client.id_verified && (
                  <button 
                    className="btn btn-success"
                    data-testid="verify-id-button"
                    onClick={() => handleVerifyID(true)}
                  >
                    Verify ID
                  </button>
                )}
                {client.id_verified && (
                  <button 
                    className="btn btn-secondary"
                    data-testid="unverify-id-button"
                    onClick={() => handleVerifyID(false)}
                  >
                    Unverify
                  </button>
                )}
                {/* Manual "Mark Complete" - forces progress to 100% and freezes the session from TTL auto-delete */}
                {!(client.manually_completed || (session?.progress ?? 0) >= 100) && (
                  <button 
                    className="btn btn-primary"
                    data-testid="mark-complete-button"
                    onClick={handleMarkComplete}
                    title="Force this record to 100% and lock it from auto-expiry"
                  >
                    ✓ Mark as Complete (100%)
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div className="card mt-3">
          <div className="card-header">
            <h3>Documents</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
            {documents.map((doc) => {
              const isIdDoc = ['id_front', 'id_back', 'selfie'].includes(doc.type);
              return (
              <div 
                key={doc._id} 
                style={{
                  border: '1px solid var(--bg-gray-200)',
                  borderRadius: '0.375rem',
                  padding: '1rem',
                  backgroundColor: 'var(--bg-gray-50)'
                }}
                data-testid={`document-${doc.type}`}
              >
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ fontSize: '0.875rem' }}>{doc.type.replace('_', ' ').toUpperCase()}</strong>
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0.25rem 0' }}>
                  {doc.filename}
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0.25rem 0' }}>
                  {new Date(doc.created_at).toLocaleDateString()}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <a 
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const token = localStorage.getItem('auth_token');
                        const response = await fetch(`${getBackendUrl()}/api/documents/${doc._id}?disposition=${isIdDoc ? 'inline' : 'attachment'}`, {
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          if (isIdDoc) {
                            window.open(url, '_blank');
                          } else {
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = doc.filename;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                          }
                        } else {
                          alert('Failed to load document');
                        }
                      } catch (err) {
                        alert('Error loading document: ' + err.message);
                      }
                    }}
                    className="btn btn-secondary"
                    data-testid={`${isIdDoc ? 'view' : 'download'}-${doc.type}`}
                    style={{ flex: 1, padding: '0.5rem', fontSize: '0.875rem', textDecoration: 'none', display: 'block', textAlign: 'center' }}
                  >
                    {isIdDoc ? 'View' : 'Download'}
                  </a>
                  {isIdDoc && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to delete this document? This cannot be undone.')) return;
                        try {
                          const token = localStorage.getItem('auth_token');
                          const response = await fetch(`${getBackendUrl()}/api/documents/${doc._id}`, {
                            method: 'DELETE',
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });
                          if (response.ok) {
                            setDocuments(prev => prev.filter(d => d._id !== doc._id));
                          } else {
                            alert('Failed to delete document');
                          }
                        } catch (err) {
                          alert('Error deleting document: ' + err.message);
                        }
                      }}
                      className="btn"
                      data-testid={`delete-${doc.type}`}
                      style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
          {documents.length === 0 && (
            <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No documents uploaded yet</p>
          )}
        </div>

        {/* Audit Log */}
        <div className="card mt-3">
          <div className="card-header">
            <h3>Activity Timeline</h3>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {auditLogs.map((log, index) => (
              <div 
                key={index} 
                style={{
                  padding: '1rem',
                  borderBottom: index < auditLogs.length - 1 ? '1px solid var(--bg-gray-200)' : 'none'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <strong style={{ fontSize: '0.875rem' }}>{log.event.replace('_', ' ').toUpperCase()}</strong>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0.25rem 0' }}>
                      By: {log.by} | IP: {log.ip}
                    </p>
                  </div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                    {new Date(log.at).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {auditLogs.length === 0 && (
            <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No activity recorded</p>
          )}
        </div>
      </main>
    </div>
  );
}

export default ClientDetail;